import React from 'react'
import { ProjectionsBulletEChart } from './echarts/echartsCharts'
// The ECharts projections chart (themed to Tellus) beside the Performance Summary
// table: the chart carries the shape of the overage, the table the exact numbers.

interface BulletDataItem {
  year?: string | number
  category?: string
  target?: number
  targetValue?: number
  actual?: number
  value?: number
  [key: string]: any
}

interface ProjectionsChartProps {
  data?: BulletDataItem[]
  minHeight?: number
  title?: string
  targetLabel?: string
  actualLabel?: string
  showTable?: boolean
}

const ProjectionsChart = ({
  data = [],
  minHeight = 500,
  title = '',
  targetLabel = 'Target',
  actualLabel = 'Actual',
  showTable = true
}: ProjectionsChartProps) => {
  if (!data || data.length === 0) {
    return (
      <div style={{ height: minHeight }} className="flex items-center justify-center rounded-lg border border-gray-200 bg-white shadow-xs">
        <div className="text-center">
          <div className="text-gray-500 text-lg">No data available</div>
        </div>
      </div>
    )
  }

  // Map to the shared ECharts projections shape { year, target, actual }.
  const chartData = data.map(item => ({
    year: item.year ?? item.category,
    target: (item.target ?? item.targetValue) as number,
    actual: (item.actual ?? item.value) as number,
  }))

  const formatNumber = (value: any) => {
    if (value === null || value === undefined || isNaN(value)) return '0'
    return parseFloat(value).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })
  }

  const chart = (
    <ProjectionsBulletEChart data={chartData} targetLabel={targetLabel} actualLabel={actualLabel} height={minHeight} />
  )

  if (!showTable) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white shadow-xs p-4">
        {chart}
      </div>
    )
  }

  return (
    <div style={{ height: minHeight }} className="flex rounded-lg border border-gray-200 bg-white shadow-xs">
      {/* Chart Section - same width + padding as the Footprint chart so the two
          charts share identical margins. */}
      <div style={{ width: '60%' }} className="flex items-center py-6 pl-8 pr-2">
        {chart}
      </div>

      {/* Table Section */}
      <div style={{ width: '40%', height: minHeight }} className="pl-4 pr-6 pt-10 flex flex-col">
        <div className="mb-4">
          <div className="text-sm font-semibold text-gray-700">Performance Summary</div>
        </div>
        <div className="flex-1 overflow-auto pt-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200">
                <th className="text-left py-2 pl-2 text-[11px] font-semibold uppercase tracking-wide text-gray-700">Year</th>
                <th className="text-right py-2 px-3 text-[11px] font-semibold uppercase tracking-wide text-gray-700">Target</th>
                <th className="text-right py-2 px-3 text-[11px] font-semibold uppercase tracking-wide text-gray-700">Actual</th>
                <th className="text-right py-2 pl-3 text-[11px] font-semibold uppercase tracking-wide text-gray-700">vs Target</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => {
                const target = item.target || item.targetValue || 0
                const actual = item.actual || item.value || 0
                const delta = actual - target
                const isMet = delta <= 0

                return (
                  <tr key={index} className="border-b border-gray-100 even:bg-gray-50 hover:bg-gray-100">
                    <td className="py-2 pl-2 font-medium text-gray-800">
                      {item.year || item.category}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums text-gray-700 whitespace-nowrap">
                      {formatNumber(target)} <span className="text-gray-400 text-xs">tCO₂e</span>
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums text-gray-700 whitespace-nowrap">
                      {formatNumber(actual)} <span className="text-gray-400 text-xs">tCO₂e</span>
                    </td>
                    {/* The signed delta against target. The sign carries the state, so it never
                        rests on colour alone, and one right-aligned tabular column lets the
                        run-up read at a glance - which "Over 3.4%" could not, sitting behind a
                        word of varying width. Cerise 600 rather than 500: the 500 drops to 4.4:1
                        on the zebra rows, under AA for text (it is fine on the chart's marks).
                        The bar chart alongside already draws the magnitude; this is the number. */}
                    <td className="py-2 pl-3 text-right">
                      <span className={`whitespace-nowrap text-xs font-semibold tabular-nums ${isMet ? 'text-perfect-shine' : 'text-cerise-red-600'}`}>
                        {delta === 0 ? '' : delta > 0 ? '+' : '−'}
                        {target ? Math.abs(delta / target * 100).toFixed(1) : '0.0'}%
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default ProjectionsChart
